import React, { useEffect, useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useSelector } from 'react-redux'

import { useTheme } from 'styled-components/native'

import { usePrevious } from '@lyrahealth-inc/shared-app-logic/src/features/common/hooks/usePrevious'
import { Appointment } from '@lyrahealth-inc/shared-app-logic/src/models/appointment/Appointment'
import { ClientListClientObject } from '@lyrahealth-inc/shared-app-logic/src/models/client/ClientListClient'
import { LoadingSkeleton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/loadingSkeleton/LoadingSkeleton'
import { Title } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/title/Title'
import { DataTable } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/dataTable/DataTable'
import { TitleSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'

import ClientListEmptyState from './ClientListEmptyState'
import { ActivityStatusItem } from './columns/ClientListActivityInfo'
import useClientListColumns from './hooks/useClientListColumns'
import { mixpanelEvents } from '../../../../../../mixpanel/mixpanelConstants'
import { track } from '../../../../../../mixpanel/mixpanelTracking'
import { useAppDispatch } from '../../../../data/store'
import { getUserClientListSorting } from '../../../../data/userSettingsSelectors'
import { setClientListSorting } from '../../../../data/userSettingsSlice'
import { useClientsData } from '../../data/hooks/useClientsData'

export type ClientItem = ClientListClientObject & {
  nextAppointment?: Appointment | null
  lastAppointment?: Appointment | null
  activityStatus?: ActivityStatusItem
}

type ClientListTableProps = {
  clients: ClientItem[] | undefined
  programIds: string[]
  providerRoles: string[]
}

const ClientListTable: React.FC<ClientListTableProps> = ({ clients, programIds, providerRoles }) => {
  const { data: activeClients, isFetching: isFetchingActiveClients } = useClientsData(
    { status: 'active' },
    { refetchOnMountOrArgChange: true },
  )
  const columns = useClientListColumns(programIds, providerRoles)
  const reduxSortingState = useSelector(getUserClientListSorting)
  const [internalSortingState, setInternalSortingState] = useState(reduxSortingState)
  const prevInternalSortingState = usePrevious(internalSortingState)

  const hasModifiedSortingState = useRef(false)
  const dispatch = useAppDispatch()
  const theme = useTheme()

  useEffect(() => {
    if (
      !hasModifiedSortingState.current ||
      (prevInternalSortingState.length === 0 && internalSortingState.length === 0)
    ) {
      return
    }
    let changedColumnId: string
    let changedColumnSortStatus: 'ascending' | 'descending' | 'inactive'

    if (internalSortingState.length > 0) {
      changedColumnId = internalSortingState[0].id
      changedColumnSortStatus = internalSortingState[0].desc ? 'descending' : 'ascending'
    } else {
      changedColumnId = prevInternalSortingState[0].id
      changedColumnSortStatus = 'inactive'
    }

    track({
      event: mixpanelEvents.CLIENT_LIST_SORT_COLUMN_HEADER,
      properties: { header_name: changedColumnId, sort_status: changedColumnSortStatus },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [internalSortingState])

  useEffect(() => {
    setInternalSortingState(reduxSortingState)
  }, [reduxSortingState])

  useEffect(() => {
    if (!hasModifiedSortingState.current) {
      return
    }
    dispatch(setClientListSorting(internalSortingState))
  }, [dispatch, internalSortingState])
  return (
    <DataTable
      columns={columns}
      data={clients}
      pagination={{ pageSize: 25 }}
      rowHeight={90}
      cellAlignment='left'
      sortingState={internalSortingState}
      onSortingChange={(sorting) => {
        hasModifiedSortingState.current = true
        setInternalSortingState(sorting)
      }}
      withRowCount
      withScroll
      pagnationStyle={{ boxShadow: `0 -4px 16px ${theme.colors.shadowLow}` }}
      emptyTableComponent={<ClientListEmptyState />}
      rightHeaderSection={
        isFetchingActiveClients ? (
          <LoadingSkeleton height={22} width={120} />
        ) : activeClients != null ? (
          <Title
            size={TitleSize.XSMALL}
            color={theme.colors.textInactive}
            text={
              <FormattedMessage
                defaultMessage=' {count, plural, one {1 active client} other {{count} active clients}}'
                description='Number of active clients'
                values={{ count: activeClients?.length }}
              />
            }
          />
        ) : null
      }
    />
  )
}

export default ClientListTable
